import { styleConstants } from '@paywithextend/components'
import React, { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  selectCreditCardIdFromRoute,
  selectPatchingCreditCardFromState,
  selectPutCreditCardAccountCausedErrorFromState,
  selectPutCreditCardBillingCausedErrorFromState,
  selectPutCreditCardErrorDetailMapFromState,
  selectPutCreditCardTopLevelErrorFromState,
  selectPuttingCreditCardAccountFromState,
  selectPuttingCreditCardBillingFromState,
  selectPuttingCreditCardFromState,
  selectReplaceCreditCardErrorDetailMapFromState,
  selectReplaceCreditCardTopLevelErrorFromState,
} from 'src/app/AccountSettings/redux/accountSettings.selectors'
import AccountBillingInformationBox, {
  AccountBillingInformationCountryFields,
  AccountBillingInformationFlatFields,
} from 'src/components/AccountBillingInformationBox/AccountBillingInformationBox'
import AccountInformationBox, {
  AccountInformationFlatFields,
} from 'src/components/AccountInformationBox/AccountInformationBox'
import AccountVerificationBlockedBox from 'src/components/AccountVerificationBlockedBox/AccountVerificationBlockedBox'
import AccountVerificationRequiredBox from 'src/components/AccountVerificationRequiredBox/AccountVerificationRequiredBox'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import CardInformationBox, {
  CardInformationBoxFlatFields,
} from 'src/components/CardInformationBox/CardInformationBox'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import TestId from 'src/components/TestId/TestId'
import VerticalContentCollection from 'src/components/VerticalContentCollection/VerticalContentCollection'
import { SupportedBrands } from 'src/enums'
import PhysicalCardTransactionsPanel from 'src/feature/PhysicalCardTransactionsPanel/PhysicalCardTransactionsPanel'
import VirtualCardCapabilitiesPanel from 'src/feature/VirtualCardCapabilitiesPanel/VirtualCardCapabilitiesPanel'
import { useBackgroundDispatch, usePrevious } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  PhysicalCardStatus,
  PhysicalCardStatusReason,
} from 'src/models/creditCard.models'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
  useStartCreditCardVerification,
  useVerifyCreditCard,
} from 'src/queries/creditCard.queries'
import {
  PUT_CREDIT_CARD_ACCOUNT_INIT,
  PUT_CREDIT_CARD_BILLING_INIT,
  putCreditCard,
  replaceCreditCard,
} from 'src/redux/actions/data/creditCards.actions'
import { useWhitelabel } from 'src/utils/whitelabel.utils'
import { css } from 'styled-components'

const AccountSettings: React.FC = () => {
  const translate = useTranslate()

  const creditCardId = useSelector(selectCreditCardIdFromRoute)
  const { data: creditCard, refetch: refetchCreditCard } = useQueryCreditCard({
    creditCardId: creditCardId,
    enabled: !!creditCardId,
  })
  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: creditCard?.id,
    enabled: !!creditCard?.id,
    retry: false,
  })

  const { data: userFeatures } = useQueryUserFeatures([
    UserFeature.PHYSICAL_CARD_TRANSACTIONS_FEED,
  ])
  const physicalCardTransactionsFeedEnabled =
    userFeatures?.[UserFeature.PHYSICAL_CARD_TRANSACTIONS_FEED] === true
  const physicalCardStatus = creditCard?.physicalCard?.status
  const hasPhysicalCard = !!physicalCardStatus
  const isPhysicalCardActive = physicalCardStatus === PhysicalCardStatus.Active

  // Gate the panel on the user feature, with an escape hatch (Active card →
  // still show, but only to allow disconnect).
  const showPhysicalCardTransactionsPanel =
    hasPhysicalCard &&
    (physicalCardTransactionsFeedEnabled || isPhysicalCardActive)
  const canConnect = physicalCardTransactionsFeedEnabled

  const verifyMutation = useVerifyCreditCard()
  const startVerificationMutation = useStartCreditCardVerification()

  const { whiteLabelIncludes } = useWhitelabel()

  const isExtendBrand = whiteLabelIncludes(SupportedBrands.Extend)

  const puttingCreditCard = useSelector(selectPuttingCreditCardFromState)
  const puttingCreditCardAccount = useSelector(
    selectPuttingCreditCardAccountFromState,
  )
  const puttingCreditCardBilling = useSelector(
    selectPuttingCreditCardBillingFromState,
  )
  const putCreditCardAccountCausedError = useSelector(
    selectPutCreditCardAccountCausedErrorFromState,
  )
  const putCreditCardBillingCausedError = useSelector(
    selectPutCreditCardBillingCausedErrorFromState,
  )
  const patchingCreditCard = useSelector(selectPatchingCreditCardFromState)
  const putCreditCardTopLevelError = useSelector(
    selectPutCreditCardTopLevelErrorFromState,
  )
  const putCreditCardErrorDetailMap = useSelector(
    selectPutCreditCardErrorDetailMapFromState,
  )
  const replaceCreditCardTopLevelError = useSelector(
    selectReplaceCreditCardTopLevelErrorFromState,
  )
  const replaceCreditCardErrorDetailMap = useSelector(
    selectReplaceCreditCardErrorDetailMapFromState,
  )
  const canUpdateCreditCard = !!creditCardPermissions?.canUpdateCreditCard

  const dispatch = useBackgroundDispatch()

  const prevPuttingAccount = usePrevious(puttingCreditCardAccount)
  const prevPuttingBilling = usePrevious(puttingCreditCardBilling)
  const prevPuttingAny = usePrevious(puttingCreditCard)

  const refreshCard = useCallback(() => {
    refetchCreditCard()
  }, [creditCardId /*, refetch */])

  useEffect(() => {
    const finished = prevPuttingAccount && !puttingCreditCardAccount
    const succeeded = finished && !putCreditCardAccountCausedError
    if (succeeded) refreshCard()
  }, [
    prevPuttingAccount,
    puttingCreditCardAccount,
    putCreditCardAccountCausedError,
    refreshCard,
  ])

  useEffect(() => {
    const finished = prevPuttingBilling && !puttingCreditCardBilling
    const succeeded = finished && !putCreditCardBillingCausedError
    if (succeeded) refreshCard()
  }, [
    prevPuttingBilling,
    puttingCreditCardBilling,
    putCreditCardBillingCausedError,
    refreshCard,
  ])

  useEffect(() => {
    const finished = prevPuttingAny && !puttingCreditCard
    const noTopLevelError =
      !replaceCreditCardTopLevelError &&
      Object.keys(replaceCreditCardErrorDetailMap ?? {}).length === 0

    if (finished && noTopLevelError) {
      refreshCard()
    }
  }, [
    prevPuttingAny,
    puttingCreditCard,
    replaceCreditCardTopLevelError,
    replaceCreditCardErrorDetailMap,
    refreshCard,
  ])

  const onBillingInformationSuccessfulSubmit = (
    flatFields: AccountBillingInformationFlatFields,
    countryFields: AccountBillingInformationCountryFields,
  ): void => {
    if (creditCard !== undefined) {
      dispatch(
        putCreditCard(
          creditCard.id,
          {
            ...creditCard,
            ...flatFields,
            ...countryFields,
          },
          false,
          {
            type: PUT_CREDIT_CARD_BILLING_INIT,
          },
        ),
      )
    }
  }

  const onAccountInformationSuccessfulSubmit = (
    flatFields: AccountInformationFlatFields,
  ): void => {
    if (creditCard !== undefined) {
      dispatch(
        putCreditCard(
          creditCard.id,
          {
            ...creditCard,
            ...flatFields,
            ...creditCard.address,
          },
          false,
          {
            type: PUT_CREDIT_CARD_ACCOUNT_INIT,
          },
        ),
      )
    }
  }

  const onCardInformationSuccessfulSubmit = (
    flatFields: CardInformationBoxFlatFields,
  ): void => {
    if (creditCard !== undefined) {
      dispatch(
        replaceCreditCard(creditCard.id, {
          ...flatFields,
        }),
      )
    }
  }

  // TODO: When physicalCard.status=BLOCKED but status=ACTIVE, we show the
  // blocked box and hide all account settings. Decide if billing/account info
  // should still be accessible since the block only affects physical card
  // verification, not the account itself.
  const showBlocked = creditCard?.isBlocked
  const showCVSVerificationFailed =
    !showBlocked &&
    creditCard?.physicalCard.status === PhysicalCardStatus.NotApplicable &&
    creditCard?.physicalCard.statusReason ===
      PhysicalCardStatusReason.CvsVerificationFailed
  const showVerificationRequired =
    !showBlocked &&
    !showCVSVerificationFailed &&
    creditCard?.physicalCard.status === PhysicalCardStatus.NeedsCVSVerification

  return creditCard !== undefined ? (
    <BackgroundPageContent>
      {showBlocked ? (
        <AccountVerificationBlockedBox
          secondParagraph={translate(
            'There have been too many failed attempts at verifying the account. In order to proceed, contact us via our ${resourceCenter}.',
            {
              resourceCenter: (
                <ResourceCenterLink
                  underline
                  css={css`
                    font-family: ${styleConstants.fontFamilies.regular};
                    font-size: 16px;
                    &:hover {
                      font-family: ${styleConstants.fontFamilies.regular};
                      font-size: 16px;
                    }
                  `}
                />
              ),
            },
          )}
        />
      ) : showCVSVerificationFailed ? (
        <AccountVerificationBlockedBox
          secondParagraph={translate(
            'Please check your card information and try enrolling again, or contact us via our ${resourceCenter}.',
            {
              resourceCenter: (
                <ResourceCenterLink
                  underline
                  css={css`
                    font-family: ${styleConstants.fontFamilies.regular};
                    font-size: 16px;
                    &:hover {
                      font-family: ${styleConstants.fontFamilies.regular};
                      font-size: 16px;
                    }
                  `}
                />
              ),
            },
          )}
        />
      ) : showVerificationRequired ? (
        <AccountVerificationRequiredBox
          accountResendVerificationError={
            startVerificationMutation.error?.error
          }
          accountVerificationError={verifyMutation.error?.error}
          canVerifyAgain={creditCard.canVerifyAgain}
          handleResendVerification={() =>
            startVerificationMutation.mutate({
              creditCardId,
              body: {} as Record<string, never>,
            })
          }
          handleVerifyCreditCard={(code) =>
            verifyMutation.mutate({ creditCardId, body: { code } })
          }
          postingAccountVerification={verifyMutation.isPending}
          postingResendAccountVerification={startVerificationMutation.isPending}
          timeUntilNextVerificationRequested={
            creditCard.timeUntilNextVerificationRequested
          }
          canUpdateCreditCard={canUpdateCreditCard}
        />
      ) : (
        <HorizontalContentCollection>
          <VerticalContentCollection>
            <AccountInformationBox
              creditCard={creditCard}
              topLevelError={putCreditCardTopLevelError}
              puttingCreditCard={puttingCreditCard}
              puttingCreditCardAccount={puttingCreditCardAccount}
              onSuccessfulSubmit={onAccountInformationSuccessfulSubmit}
              hasEditPermissions={canUpdateCreditCard}
              inFocus={putCreditCardAccountCausedError}
            />
            <AccountBillingInformationBox
              creditCard={creditCard}
              puttingCreditCard={puttingCreditCard}
              puttingCreditCardBilling={puttingCreditCardBilling}
              topLevelError={putCreditCardTopLevelError}
              errorDetailMap={putCreditCardErrorDetailMap}
              onSuccessfulSubmit={onBillingInformationSuccessfulSubmit}
              hasEditPermissions={canUpdateCreditCard}
              inFocus={putCreditCardBillingCausedError}
            />
          </VerticalContentCollection>
          <VerticalContentCollection>
            {!creditCard.partner?.isVirtualAccount && (
              <TestId testId={'cardInformationBox'}>
                <CardInformationBox
                  creditCard={creditCard}
                  puttingCreditCard={puttingCreditCard}
                  patchingCreditCard={patchingCreditCard}
                  topLevelError={replaceCreditCardTopLevelError}
                  errorDetailMap={replaceCreditCardErrorDetailMap}
                  onSuccessfulSubmit={onCardInformationSuccessfulSubmit}
                  hasEditPermissions={
                    creditCard.features.rcnReplacementEnabled &&
                    canUpdateCreditCard
                  }
                />
              </TestId>
            )}
            {isExtendBrand && showPhysicalCardTransactionsPanel && (
              <PhysicalCardTransactionsPanel
                creditCard={creditCard}
                canConnect={canConnect}
              />
            )}
            {isExtendBrand && (
              <VirtualCardCapabilitiesPanel creditCard={creditCard} />
            )}
          </VerticalContentCollection>
        </HorizontalContentCollection>
      )}
    </BackgroundPageContent>
  ) : (
    <NotFoundBackgroundPageContent />
  )
}

export default AccountSettings
